/**
 * Converting one kind of thing into another (spec: never lose input). Each
 * conversion creates the target, carries over what maps, links what it can,
 * and retires the source with a `convert` action ("became X") rather than a
 * delete. Everything is ordinary ops through the Mutator, so it syncs and the
 * assistant could propose the same.
 */
import { db, mutate } from '@/runtime'
import type { Resource } from '@/db/types'
import type { TaskResource } from '@/kinds/task/apply'
import type { ProjectResource } from '@/kinds/project/apply'
import type { ListResource } from '@/kinds/list/apply'
import type { PageResource } from '@/kinds/page/apply'
import { isOpen } from '@/kinds/task/schema'
import { nextRank } from '@/kinds/page/schema'

export type Target = 'Project' | 'Page' | 'List' | 'Task' | 'Tasks'
export type Source = TaskResource | ProjectResource | ListResource | PageResource

/** The conversions offered for a kind, with a one-line preview of what carries over. */
export const conversionsFor = (
  r: Source,
  ctx: { tasks: TaskResource[] },
): { to: Target; label: string; preview: string }[] => {
  const spec = r.spec as Record<string, unknown>
  switch (r.kind) {
    case 'Task': {
      const t = r as TaskResource
      const steps = (t.spec.checklist ?? []).filter((c) => !c.done).length
      return [
        {
          to: 'Project',
          label: 'Project',
          preview: `A project “${t.spec.title}”${steps ? `, with ${steps} task${steps === 1 ? '' : 's'} from the checklist` : ''}${t.spec.due ? ', same due date' : ''}. Notes, labels, pages and lists come along.`,
        },
        {
          to: 'Page',
          label: 'Page',
          preview: `A page “${t.spec.title}” with the notes as its body${steps ? ' and the checklist as tick boxes' : ''}.`,
        },
        {
          to: 'List',
          label: 'List',
          preview: steps
            ? `A list “${t.spec.title}” with the ${steps} open checklist step${steps === 1 ? '' : 's'} as items.`
            : `A list “${t.spec.title}” with the task itself as its first item.`,
        },
      ]
    }
    case 'Project': {
      const p = r as ProjectResource
      const open = ctx.tasks.filter((t) => isOpen(t.status.state) && !t.deleted_at)
      return [
        {
          to: 'Task',
          label: 'Task',
          preview: `One task “${p.spec.title}”${open.length ? ` with the ${open.length} open task${open.length === 1 ? '' : 's'} as its checklist` : ''}. Pages and lists move under it.`,
        },
        {
          to: 'Page',
          label: 'Page',
          preview: `A page “${p.spec.title}” with the notes and outcome as its body${open.length ? ' and the open tasks as tick boxes' : ''}.`,
        },
      ]
    }
    case 'List': {
      const l = r as ListResource
      const open = l.spec.items.filter((i) => !i.done).length
      return [
        {
          to: 'Tasks',
          label: 'Tasks',
          preview: `${open} inbox task${open === 1 ? '' : 's'}, one per open item, each linked back here.`,
        },
        {
          to: 'Project',
          label: 'Project',
          preview: `A project “${l.spec.title}” with the ${open} open item${open === 1 ? '' : 's'} as its tasks.`,
        },
        { to: 'Page', label: 'Page', preview: `A page “${l.spec.title}” with the items as tick boxes.` },
      ]
    }
    case 'Page': {
      const p = r as PageResource
      const boxes = checklistLines(p.spec.text).filter((c) => !c.done).length
      return [
        {
          to: 'Project',
          label: 'Project',
          preview: `A project “${p.name}”${boxes ? ` with the ${boxes} open tick box${boxes === 1 ? '' : 'es'} as tasks` : ''}. The page becomes the project’s page.`,
        },
        {
          to: 'List',
          label: 'List',
          preview: `A list of the page’s tick boxes${boxes ? ` (${boxes} open)` : ''}, kept under the page.`,
        },
        { to: 'Task', label: 'Task', preview: `A task “${p.name}” with the page under it.` },
      ]
    }
    default:
      void spec
      return []
  }
}

/** "- [ ] x" and "- [x] x" lines in a page's text projection. */
export const checklistLines = (text: string): { text: string; done: boolean }[] =>
  text
    .split('\n')
    .map((l) => /^\s*(?:[-*]\s*)?\[( |x|X)\]\s+(.+)$/.exec(l))
    .filter((m): m is RegExpExecArray => !!m)
    .map((m) => ({ text: m[2]!.trim(), done: m[1] !== ' ' }))

const wsOf = (r: Resource): Record<string, string> => (r.labels.ws ? { ws: r.labels.ws } : {})
const carryLabels = (r: Resource): Record<string, string> => {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(r.labels)) if (k !== 'ws') out[k] = v
  return out
}

/** Re-parent every page and list that belongs to `from` so it belongs to `to`. */
const moveChildren = async (from: string, to: string) => {
  const edges = await db.edges.where('to_id').equals(from).toArray()
  for (const e of edges) {
    if (e.relation !== 'belongs_to' || e.deleted_at) continue
    const child = await db.resources.get(e.from_id)
    if (child && (child.kind === 'Page' || child.kind === 'List')) await mutate.setProject(child.id, to)
  }
}

const md = (title: string, body: string | undefined, boxes: { text: string; done: boolean }[]) =>
  [
    `# ${title}`,
    body?.trim() ? `\n${body.trim()}` : '',
    boxes.length ? `\n${boxes.map((b) => `- [${b.done ? 'x' : ' '}] ${b.text}`).join('\n')}` : '',
  ]
    .filter(Boolean)
    .join('\n')

/** Run a conversion. Returns where to go next. */
export const convert = async (
  r: Source,
  to: Target,
  ctx: { tasks: TaskResource[] },
): Promise<{ kind: string; id: string }> => {
  const ws = wsOf(r)
  const labels = { ...ws, ...carryLabels(r) }
  const became = (kind: string, id: string) => mutate.action(r.id, 'convert', { kind, id })
  switch (r.kind) {
    case 'Task': {
      const t = r as TaskResource
      const steps = t.spec.checklist ?? []
      if (to === 'Project') {
        const p = await mutate.create(
          'Project',
          {
            title: t.spec.title,
            ...(t.spec.notes ? { notes: t.spec.notes } : {}),
            ...(t.spec.due ? { due: t.spec.due } : {}),
          },
          labels,
        )
        for (const c of steps) {
          if (c.done) continue
          const nt = await mutate.create('Task', { title: c.text }, ws)
          await mutate.setProject(nt.id, p.id)
        }
        await moveChildren(t.id, p.id)
        await became('Project', p.id)
        return { kind: 'Project', id: p.id }
      }
      if (to === 'Page') {
        const pg = await mutate.create(
          'Page',
          {
            title: t.spec.title,
            blocks: [],
            text: md(t.spec.title, t.spec.notes, steps),
            pinned: false,
            rank: nextRank([]),
          },
          ws,
        )
        await moveChildren(t.id, pg.id)
        await became('Page', pg.id)
        return { kind: 'Page', id: pg.id }
      }
      if (to === 'List') {
        const items = (
          steps.filter((c) => !c.done).length
            ? steps.filter((c) => !c.done).map((c) => c.text)
            : [t.spec.title]
        ).map((text) => ({ id: crypto.randomUUID(), text, done: false }))
        const l = await mutate.create(
          'List',
          { title: t.spec.title, style: 'checklist', items, pinned: false },
          ws,
        )
        await moveChildren(t.id, l.id).catch(() => {})
        await became('List', l.id)
        return { kind: 'List', id: l.id }
      }
      break
    }
    case 'Project': {
      const p = r as ProjectResource
      const open = ctx.tasks.filter((t) => isOpen(t.status.state) && !t.deleted_at)
      if (to === 'Task') {
        const nt = await mutate.create(
          'Task',
          {
            title: p.spec.title,
            ...(p.spec.notes || p.spec.outcome
              ? { notes: [p.spec.outcome, p.spec.notes].filter(Boolean).join('\n\n') }
              : {}),
            ...(p.spec.due ? { due: p.spec.due } : {}),
            ...(open.length ? { checklist: open.map((t) => ({ text: t.spec.title, done: false })) } : {}),
          },
          labels,
        )
        for (const t of open)
          await mutate.action(t.id, 'convert', { kind: 'Task', id: nt.id }).catch(() => {})
        await moveChildren(p.id, nt.id)
        await became('Task', nt.id)
        return { kind: 'Task', id: nt.id }
      }
      if (to === 'Page') {
        const pg = await mutate.create(
          'Page',
          {
            title: p.spec.title,
            blocks: [],
            text: md(
              p.spec.title,
              [p.spec.outcome, p.spec.notes].filter(Boolean).join('\n\n'),
              open.map((t) => ({ text: t.spec.title, done: false })),
            ),
            pinned: false,
            rank: nextRank([]),
          },
          ws,
        )
        for (const t of open)
          await mutate.action(t.id, 'convert', { kind: 'Page', id: pg.id }).catch(() => {})
        await moveChildren(p.id, pg.id)
        await became('Page', pg.id)
        return { kind: 'Page', id: pg.id }
      }
      break
    }
    case 'List': {
      const l = r as ListResource
      const open = l.spec.items.filter((i) => !i.done)
      if (to === 'Tasks') {
        for (const it of open) {
          const nt = await mutate.create('Task', { title: it.text }, ws)
          await mutate.setEdge(nt.id, l.id, 'references')
        }
        await became('Task', r.id)
        return { kind: 'Inbox', id: '' }
      }
      if (to === 'Project') {
        const p = await mutate.create('Project', { title: l.spec.title }, labels)
        for (const it of open) {
          const nt = await mutate.create('Task', { title: it.text }, ws)
          await mutate.setProject(nt.id, p.id)
        }
        await became('Project', p.id)
        return { kind: 'Project', id: p.id }
      }
      if (to === 'Page') {
        const pg = await mutate.create(
          'Page',
          {
            title: l.spec.title,
            blocks: [],
            text: md(
              l.spec.title,
              undefined,
              l.spec.items.map((i) => ({ text: i.text, done: i.done })),
            ),
            pinned: false,
            rank: nextRank([]),
          },
          ws,
        )
        await became('Page', pg.id)
        return { kind: 'Page', id: pg.id }
      }
      break
    }
    case 'Page': {
      const p = r as PageResource
      const boxes = checklistLines(p.spec.text)
      if (to === 'Project') {
        const pr = await mutate.create(
          'Project',
          { title: p.name, ...(p.spec.text.trim() ? { notes: p.spec.text.trim().slice(0, 2000) } : {}) },
          labels,
        )
        for (const b of boxes) {
          if (b.done) continue
          const nt = await mutate.create('Task', { title: b.text }, ws)
          await mutate.setProject(nt.id, pr.id)
        }
        await mutate.setProject(p.id, pr.id)
        return { kind: 'Project', id: pr.id }
      }
      if (to === 'List') {
        const items = boxes.map((b) => ({ id: crypto.randomUUID(), text: b.text, done: b.done }))
        const l = await mutate.create('List', { title: p.name, style: 'checklist', items, pinned: false }, ws)
        await mutate.setEdge(l.id, p.id, 'belongs_to')
        return { kind: 'List', id: l.id }
      }
      if (to === 'Task') {
        const nt = await mutate.create(
          'Task',
          { title: p.name, ...(p.spec.text.trim() ? { notes: p.spec.text.trim().slice(0, 4000) } : {}) },
          labels,
        )
        await mutate.setProject(p.id, nt.id)
        return { kind: 'Task', id: nt.id }
      }
      break
    }
  }
  throw new Error(`cannot convert ${r.kind} to ${to}`)
}

/** Route for a target. */
export const routeFor = (
  kind: string,
): '/projects/$id' | '/pages/$id' | '/lists/$id' | '/tasks/$id' | '/inbox' =>
  kind === 'Project'
    ? '/projects/$id'
    : kind === 'Page'
      ? '/pages/$id'
      : kind === 'List'
        ? '/lists/$id'
        : kind === 'Task'
          ? '/tasks/$id'
          : '/inbox'
